import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import { FormDocumentRow } from '../../../components/Form/FormDocumentRow';
import {
  fetchLegalAdviceById,
  startReviewLegalAdviceForm,
  uploadFileService,
} from '../../../services/LegalAdviceSixService';
import { submitAttachmentAction } from '../../../services/genericFormServices';
import { DetailHeader } from '../../../components/Form/DetailHeader';
import { ActionSidebar, isCurrentUserOrigin, type AttachmentItem } from '../../../components/Form/ActionSideBar';
import { LegalDetailFormSixDocument } from './LegalDetailFormSixDocument';
import { StageProgression } from '../../../components/Form/StateProgression';
import type { LegalAdviceFormSixData } from '../../../types/LegalAdvices/LegalAdviceFormSix.type';
import type { GeoLevel } from '../../../types/database';


export type MixedAttachment = File | AttachmentItem;

export interface CreateAttachmentDto {
  fileName: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  remarks?: string;
}


export interface LegalFormSixDetailViewProps {
  id?: string;
  onBack?: () => void;
}

export function LegalFormSixDetailView({
  id: propId,
  onBack,
}: LegalFormSixDetailViewProps) {
  const navigate = useNavigate();
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const params = useParams<{ id: string }>();

  // Use propId if passed, otherwise grab it from the URL route
  const id = propId || params.id;

  const [isSigning, setIsSigning] = useState(false);
  const [remark, setRemark] = useState('');
  const [loading, setLoading] = useState<boolean>(Boolean(id));
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize with empty form state or null
  const [formData, setFormData] = useState<LegalAdviceFormSixData | null>(null);

  // Fetch record from API whenever `id` is present
  useEffect(() => {
    if (!id) return;

    let isSubscribed = true;
    setLoading(true);
    setError(null);

    fetchLegalAdviceById(id)
      .then((data: any) => {
        if (isSubscribed) {
          // Normalize nested form values if wrapped inside legalAdviceFormSix property
          const normalizedData: LegalAdviceFormSixData = {
            ...data,
            ...(data.legalAdviceFormSix || {}),
          };
          setFormData(normalizedData);
        }
      })
      .catch((err: any) => {
        if (isSubscribed) {
          setError(err?.message || 'အချက်အလက်များ ရယူ၍ မရပါ');
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [id]);

  // Handle Loading State
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-3 font-sans">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
        <p className="text-sm font-medium text-slate-600">
          အချက်အလက်များကို ရယူနေပါသည်...
        </p>
      </div>
    );
  }

  // Handle Missing or Error State
  if (!formData) {
    return (
      <div className="p-6 text-center text-rose-600 font-sans">
        {error || 'အချက်အလက် မရှိပါ။ (Form data not found)'}
      </div>
    );
  }

  // Form editability condition
  const isOriginUser = isCurrentUserOrigin(formData);
  const isEditable =
    !isOriginUser &&
    (formData.status === 'UNDER_REVIEW' || formData.status === 'DRAFT');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!isEditable) return;
    const { name, value } = e.target;
    setFormData((prev) => (prev ? { ...prev, [name]: value } : prev));
  };

  const handleClearDate = (name: keyof LegalAdviceFormSixData) => {
    if (!isEditable) return;
    setFormData((prev) => (prev ? { ...prev, [name]: '' } : prev));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (
      isOriginUser ||
      (!isEditable && formData.status !== 'ATTACHED' && formData.status !== 'SUBMITTED') ||
      !e.target.files
    ) return;
    const filesArray = Array.from(e.target.files);
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            attachments: [...(prev.attachments || []), ...filesArray],
          }
        : prev
    );
  };

  const handleRemoveFile = (index: number) => {
    if (
      isOriginUser ||
      (!isEditable &&
      formData.status !== 'ATTACHED' &&
      formData.status !== 'SUBMITTED')
    ) return;
    setFormData((prev) =>
      prev
        ? {
            ...prev,
            attachments: (prev.attachments || []).filter((_, i) => i !== index),
          }
        : prev
    );
  };

  const handleSaveSignature = () => {
    if (!isEditable) return;
    if (sigCanvasRef.current) {
      if (sigCanvasRef.current.isEmpty()) {
        alert('လက်မှတ် အရင်ရေးထိုးပေးပါရန်။');
        return;
      }
      try {
        const canvas = sigCanvasRef.current.getTrimmedCanvas();
        const base64Data = canvas.toDataURL('image/png');
        setFormData((prev) =>
          prev ? { ...prev, signatureImage: base64Data } : prev
        );
        setIsSigning(false);
      } catch {
        const rawCanvas = sigCanvasRef.current.getCanvas();
        const base64Data = rawCanvas.toDataURL('image/png');
        setFormData((prev) =>
          prev ? { ...prev, signatureImage: base64Data } : prev
        );
        setIsSigning(false);
      }
    }
  };

  const handleBackNavigation = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  // ---------------------------------------------------------------------------
  // Action Handlers (Global Document Operations)
  // ---------------------------------------------------------------------------

  const handleStartReviewAction = async () => {
    const targetId = id || formData?.id;
    if (!targetId) return;

    setSaving(true);
    setError(null);
    try {
      await startReviewLegalAdviceForm(targetId);
      setFormData((prev) =>
        prev ? { ...prev, status: 'UNDER_REVIEW' } : prev
      );
      setFormData(await fetchLegalAdviceById(targetId));
      alert('စိစစ်ဆောင်ရွက်ရန် အောင်မြင်ပါသည်');
    } catch (err: any) {
      setError(err?.message || 'စိစစ်ဆောင်ရွက်ရန် မအောင်မြင်ပါ');
    } finally {
      setSaving(false);
    }
  };

  const handleAttachmentAction = async (
    targetGeoLevels: GeoLevel[],
    forwardGeoLevels: GeoLevel[]
  ) => {
    if (isOriginUser) return;
    const targetId = id || formData?.id;
    if (!targetId) {
      setError('အမှုအမှတ် မရှိပါ (Case ID is missing)');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const filesToUpload: File[] = [];

      // 1. Separate new raw File objects from already uploaded items
      (formData.attachments || []).forEach((item: any) => {
        if (item instanceof File) {
          filesToUpload.push(item);
        }
      });

      // 2. Upload new files concurrently
      const newlyUploadedAttachments: CreateAttachmentDto[] =
        await Promise.all(
          filesToUpload.map(async (file) => {
            const uploadRes = await uploadFileService(file);
            return {
              fileName: file.name,
              fileUrl: uploadRes.fileUrl,
              fileSize: file.size,
              mimeType: file.type,
              remarks: remark || undefined,
            };
          })
        );

      await submitAttachmentAction(
        targetId,
        remark,
        newlyUploadedAttachments,
        targetGeoLevels,
        forwardGeoLevels
      );

      setFormData((prev) =>
        prev
          ? {
              ...prev,
              status: forwardGeoLevels.length > 0 ? 'SUBMITTED' : 'ATTACHED',
              attachments: [],
              targetGeoLevels,
              forwardedToLevels: forwardGeoLevels.length
                ? forwardGeoLevels
                : undefined,
            }
          : prev
      );
      setFormData(await fetchLegalAdviceById(targetId));
      setRemark('');
      alert('ဖိုင်တွဲ သိမ်းဆည်းခြင်း အောင်မြင်ပါသည်။ (Attached Successfully)');
    } catch (err: any) {
      setError(err?.message || 'ဖိုင်တွဲ သိမ်းဆည်းခြင်း မအောင်မြင်ပါ');
    } finally {
      setSaving(false);
    }
  };

  const renderDocumentRow = (
  label: string,
  name: Extract<keyof LegalAdviceFormSixData, string>
) => (
  <FormDocumentRow
    label={label}
    name={name}
    value={(formData[name] as string) || ''}
    isEditable={isEditable}
    onChange={handleChange}
  />
);

const renderDatePicker = (
  name: Extract<keyof LegalAdviceFormSixData, string>
) => (
  <FormDatePicker
    name={name}
    value={(formData?.[name] as string) || ''}
    isEditable={isEditable}
    onChange={handleChange}
    onClear={handleClearDate}
  />
);

  const renderStageProgression = () => (
    <StageProgression
      status={formData.status}
      error={error}
      onErrorDismiss={() => setError(null)}
      targetGeoLevels={formData.targetGeoLevels}
      originOfficeName={formData.originOffice?.name}
      originGeoLevel={'originGeoLevel' in formData ? formData.originGeoLevel : undefined}
      auditLogs={formData.auditLogs}
      stageHistory={formData.stageHistory}
    />
  );

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 font-sans bg-slate-50 min-h-screen space-y-6">
      {/* Top Header Navigation Bar */}
      <DetailHeader
        title=""
        caseNumber={formData.letterNo}
        isEditable={isEditable}
        saving={saving}
        onBack={handleBackNavigation}
      />

      {renderStageProgression()}

      {/* Main Grid Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT SECTION: Document View */}
        <div className="lg:col-span-8">
          <LegalDetailFormSixDocument
            formData={formData}
            isEditable={isEditable}
            isSigning={isSigning}
            sigCanvasRef={sigCanvasRef}
            setIsSigning={setIsSigning}
            handleChange={handleChange}
            handleSaveSignature={handleSaveSignature}
            renderDocumentRow={renderDocumentRow}
            renderDatePicker={renderDatePicker}
            handleBackNavigation={handleBackNavigation}
          />
        </div>

        {/* RIGHT SECTION: Sidebar View */}
        <div className="lg:col-span-4">
          <ActionSidebar
            formData={formData}
            saving={saving}
            remark={remark}
            setRemark={setRemark}
            attachments={formData.attachments as any}
            onFileChange={handleFileChange}
            onRemoveFile={handleRemoveFile}
            onSave={handleAttachmentAction}
            onStartReview={handleStartReviewAction}
          />
        </div>
      </div>
    </div>
  );
}